import React, { useEffect, useMemo, useState } from 'react'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import ApartmentIcon from '@mui/icons-material/Apartment'
import GroupsIcon from '@mui/icons-material/Groups'
import SendIcon from '@mui/icons-material/Send'
import DraftsIcon from '@mui/icons-material/Drafts'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AdsClickIcon from '@mui/icons-material/Mouse'
import ReplyIcon from '@mui/icons-material/Reply'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CategoryIcon from '@mui/icons-material/Category'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import GridViewIcon from '@mui/icons-material/GridView'
import { useGetEmailMarketingQuery } from '../../redux/growthosEmailMarketingApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import {
  GX_BORDER, GX_INK_500, GX_INK_900, GX_COLORS, GX_PAGE_SX, GX_NEGATIVE,
  GxKpiCard, GxSectionCard, GxPill, GxFunnelStage, GxSyncStatusBar,
} from '../growthos-shared/GrowthOsUi'
import { GxCollapsePanel, GxPagedTable, gxInt } from '../growthos-shared/GxPagedTable'
import GxRequestCampaignSection from '../growthos-shared/GxRequestCampaignSection'
import { InlineCompanyTable, InlineContactTable } from './EmailDrillTables'

/* Port of the reference app/(dashboard)/analytics/email-marketing/page.tsx:
   KPI row (Companies / Contacts Reached / Emails Delivered / Opened / Clicked / Replied — the
   clickable ones open an inline contact or company drill), tapering Engagement Funnel (each stage
   drills; Bounced is a side badge, not a taper stage), By Category + By Sales POC tables (click a
   row -> that segment's contacts), Category × Sales POC matrix, Request a Campaign. */

// Presentational default — reference ops_email_marketing_config.outreach_start_date.
const DEFAULT_FROM = '2026-05-21'

const PRESETS = [
  { key: 'default', label: 'May 21 – Today' },
  { key: 'month', label: 'This Month' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'custom', label: 'Custom' },
]

const SYNC_LABELS = { hubspot: 'HubSpot', hubspot_api: 'HubSpot' }

const FUNNEL_TAPER_ORDER = ['Sent', 'Opened', 'Opened 2+', 'Clicked', 'Replied']
const FUNNEL_DISPLAY_LABEL = { Sent: 'Contacts Reached' }
const FUNNEL_METRIC_KEY = { Sent: 'sent', Bounced: 'bounced', Opened: 'opened', 'Opened 2+': 'opened_2plus', Clicked: 'clicked', Replied: 'replied' }
const FUNNEL_META = {
  Sent: { icon: <SendIcon sx={{ fontSize: 18 }} />, gradient: 'linear-gradient(135deg,#4F46E5,#06B6D4)' },
  Opened: { icon: <DraftsIcon sx={{ fontSize: 18 }} />, gradient: 'linear-gradient(135deg,#0D9488,#34D399)' },
  'Opened 2+': { icon: <VisibilityIcon sx={{ fontSize: 18 }} />, gradient: 'linear-gradient(135deg,#0EA5E9,#67E8F9)' },
  Clicked: { icon: <AdsClickIcon sx={{ fontSize: 18 }} />, gradient: 'linear-gradient(135deg,#D97706,#FBBF24)' },
  Replied: { icon: <ReplyIcon sx={{ fontSize: 18 }} />, gradient: 'linear-gradient(135deg,#7C3AED,#C084FC)' },
}
// Square-root scale + floor so the long tail (Replied is often <1% of Sent) stays readable.
const funnelWidthPct = (pctOfSent) => {
  const p = Math.max(0, Math.min(100, pctOfSent ?? 0)) / 100
  return 24 + (100 - 24) * Math.sqrt(p)
}

const fmtDay = (iso) => (iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '')
const pad = (n) => String(n).padStart(2, '0')
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const todayIso = () => isoDay(new Date())
const daysAgoIso = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d) }
const fmtRate = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`)
const changePct = (cur, prev) => (prev > 0 ? Math.round(((cur - prev) / prev) * 1000) / 10 : null)
const comparison = (cur, prev) => {
  if (prev == null) return undefined
  const ch = changePct(cur, prev)
  return `Previous: ${gxInt(prev)}${ch == null ? '' : ` · ${ch >= 0 ? '↑' : '↓'} ${Math.abs(ch).toFixed(1)}%`}`
}
const joinSub = (...parts) => { const p = parts.filter(Boolean); return p.length ? p.join(' · ') : undefined }

function ClickableFunnelStage({ width, onClick, ...stageProps }) {
  return (
    <Box
      component="button" onClick={onClick}
      sx={{
        display: 'block', width: `${width}%`, mx: 'auto', p: 0, border: 'none', background: 'none', cursor: 'pointer',
        transition: 'transform 200ms', '&:hover': { transform: 'scale(1.02)' },
      }}
    >
      <GxFunnelStage {...stageProps} width={100} />
    </Box>
  )
}

function SegmentTable({ title, segmentLabel, rows, makeRequest, icon }) {
  const [openKey, setOpenKey] = useState(null)
  const pctCell = (n, p) => (
    <>{gxInt(n)}{p != null && <Typography component="span" sx={{ ml: 0.5, fontSize: 11, color: GX_INK_500 }}>({fmtRate(p)})</Typography>}</>
  )
  const keyOf = (r) => r.key || r.label
  return (
    <GxCollapsePanel title={title} count={rows.length} icon={icon}>
      <Box sx={{ p: 1.5 }}>
        <GxPagedTable
          rows={rows}
          rowKey={keyOf}
          filter={(r, q) => keyOf(r).toLowerCase().includes(q)}
          filterPlaceholder={`Filter by ${segmentLabel.toLowerCase()}…`}
          emptyText="No data in this window."
          onRowClick={(r) => setOpenKey((k) => (k === keyOf(r) ? null : keyOf(r)))}
          isRowActive={(r) => keyOf(r) === openKey}
          columns={[
            { key: 'seg', header: segmentLabel, title: `${segmentLabel} — click to drill into contacts.`, sortValue: keyOf, cell: (r) => <Typography sx={{ fontSize: 12, fontWeight: 600, color: GX_INK_900 }}>{keyOf(r)}</Typography> },
            { key: 'companies', header: 'Companies', title: 'Distinct resolved companies in this segment.', align: 'right', sortValue: (r) => r.companies, cell: (r) => gxInt(r.companies) },
            { key: 'sent', header: 'Sent', title: 'Contacts reached in this segment.', align: 'right', sortValue: (r) => r.sent, cell: (r) => gxInt(r.sent) },
            { key: 'bounced', header: 'Bounced', title: 'Contacts with a bounce (rate = of reached).', align: 'right', sortValue: (r) => r.bounced, cell: (r) => pctCell(r.bounced, r.bouncedPct) },
            { key: 'opened', header: 'Opened', title: 'Contacts who opened ≥1 email (rate = of reached).', align: 'right', sortValue: (r) => r.opened, cell: (r) => pctCell(r.opened, r.openedPct) },
            { key: 'opened2plus', header: 'Opened 2+', title: 'Contacts who opened ≥2 emails.', align: 'right', sortValue: (r) => r.opened2Plus, cell: (r) => gxInt(r.opened2Plus) },
            { key: 'clicked', header: 'Clicked', title: 'Contacts who clicked ≥1 email (rate = of reached).', align: 'right', sortValue: (r) => r.clicked, cell: (r) => pctCell(r.clicked, r.clickedPct) },
            { key: 'replied', header: 'Replied', title: 'Contacts who replied ≥1 email (rate = of reached).', align: 'right', sortValue: (r) => r.replied, cell: (r) => pctCell(r.replied, r.repliedPct) },
          ]}
        />
        {openKey && <Box sx={{ mt: 1.5 }}><InlineContactTable request={makeRequest(openKey)} onClose={() => setOpenKey(null)} /></Box>}
      </Box>
    </GxCollapsePanel>
  )
}

// Category × Sales POC grid — cells are CONTACT counts.
function MatrixTable({ leaders, rows }) {
  return (
    <GxCollapsePanel title="Category × Sales POC — Contacts" count={rows.length} icon={<GridViewIcon sx={{ fontSize: 16 }} />}>
      <Box sx={{ p: 1.5 }}>
        <GxPagedTable
          rows={rows}
          rowKey={(r) => r.category}
          filter={(r, q) => r.category.toLowerCase().includes(q)}
          filterPlaceholder="Filter by category…"
          emptyText="No data in this window."
          columns={[
            { key: 'category', header: 'Category', title: 'Target category.', sortValue: (r) => r.category, cell: (r) => <Typography sx={{ fontSize: 12, fontWeight: 600, color: GX_INK_900 }}>{r.category}</Typography> },
            ...leaders.map((l) => ({
              key: `leader:${l}`, header: l, align: 'right',
              sortValue: (r) => r.counts?.[l] ?? 0,
              cell: (r) => gxInt(r.counts?.[l] ?? 0),
            })),
          ]}
        />
      </Box>
    </GxCollapsePanel>
  )
}

export default function EmailMarketingPage() {
  const [presetKey, setPresetKey] = useState('default')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [kpiDrill, setKpiDrill] = useState(null)
  const [funnelDrill, setFunnelDrill] = useState(null)
  const [companyDrillOpen, setCompanyDrillOpen] = useState(false)

  const rangeParams = useMemo(() => {
    if (presetKey === 'month') return { period: 'mtd', from: undefined, to: undefined }
    if (presetKey === 'last30') return { period: 'custom', from: daysAgoIso(30), to: todayIso() }
    if (presetKey === 'last7') return { period: 'custom', from: daysAgoIso(7), to: todayIso() }
    if (presetKey === 'custom') return { period: 'custom', from: customFrom, to: customTo }
    return { period: 'custom', from: DEFAULT_FROM, to: todayIso() }
  }, [presetKey, customFrom, customTo])
  const customReady = presetKey !== 'custom' || Boolean(customFrom && customTo)
  const qsKey = JSON.stringify(rangeParams)

  // A drill describes the selected period; never leave a stale one open after the filter changes.
  useEffect(() => { setKpiDrill(null); setFunnelDrill(null); setCompanyDrillOpen(false) }, [qsKey])

  const { data, error, isFetching } = useGetEmailMarketingQuery(rangeParams, { skip: !customReady })
  const { data: runsData } = useListIngestRunsQuery()

  const makeDrill = (metric, label, extra) => ({ metric, label, ...rangeParams, ...(extra || {}) })
  const toggleKpiDrill = (metric, label) => {
    setKpiDrill((cur) => (cur?.metric === metric ? null : makeDrill(metric, label)))
    setFunnelDrill(null); setCompanyDrillOpen(false)
  }
  const toggleFunnelDrill = (metric, label) => {
    setFunnelDrill((cur) => (cur?.metric === metric ? null : makeDrill(metric, label)))
    setKpiDrill(null); setCompanyDrillOpen(false)
  }
  const toggleCompanyDrill = () => { setCompanyDrillOpen((o) => !o); setKpiDrill(null); setFunnelDrill(null) }

  // The default "May 21 – Today" preset has no meaningful prior window (it predates outreach) —
  // comparisons are suppressed there, like the reference.
  const prev = presetKey === 'default' ? null : data?.previous
  const cmp = (cur, key) => (prev ? comparison(cur, prev[key]) : undefined)
  const reached = data?.contactsReached || 0
  const rateOfReached = (v) => (v != null && reached > 0 ? `${fmtRate((v / reached) * 100)} of contacts reached` : undefined)
  const loadingValue = isFetching && !data ? '…' : '—'

  const funnel = data?.funnel || []
  const bounced = funnel.find((f) => f.stage === 'Bounced')
  let prevWidth = 100
  const taper = FUNNEL_TAPER_ORDER.map((stage) => {
    const f = funnel.find((row) => row.stage === stage)
    if (!f) return null
    // Independent flags (a contact can click without opening twice): clamp each bar to be no wider
    // than the bar above so the shape stays a funnel; printed counts stay true.
    const width = Math.min(funnelWidthPct(f.pctOfSent), prevWidth)
    prevWidth = width
    return { ...f, width }
  }).filter(Boolean)

  const windowText = data?.window ? `${fmtDay(data.window.from)} – ${fmtDay(data.window.to)}` : ''

  return (
    <Box sx={GX_PAGE_SX}>
      <GxSyncStatusBar runs={runsData?.runs || []} labels={SYNC_LABELS} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <MailOutlineIcon sx={{ fontSize: 14, color: GX_COLORS.indigo }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: GX_COLORS.indigo }}>HubSpot Contacts · live</Typography>
          </Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>Email Marketing</Typography>
          <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.5, maxWidth: 760 }}>
            Reproduces the Email Outreach Engine sheet, sourced live from HubSpot Contacts. Counts are
            contacts whose last marketing-email send falls in the window; engagement counters are each
            contact&apos;s lifetime totals. Sync via Data → Connectors.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select size="small" value={presetKey} onChange={(e) => setPresetKey(e.target.value)} sx={{ minWidth: 176, fontSize: 13, bgcolor: '#fff' }}>
            {PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
          </Select>
          {presetKey === 'custom' && (
            <>
              <TextField size="small" type="date" value={customFrom} inputProps={{ min: DEFAULT_FROM }} onChange={(e) => setCustomFrom(e.target.value)} />
              <Typography sx={{ color: GX_INK_500 }}>→</Typography>
              <TextField size="small" type="date" value={customTo} inputProps={{ min: DEFAULT_FROM }} onChange={(e) => setCustomTo(e.target.value)} />
            </>
          )}
          {windowText && <GxPill color={GX_COLORS.indigo}>Showing: {windowText}</GxPill>}
        </Box>
      </Box>

      {error && (
        <Box sx={{ border: '1px solid #fecdd3', bgcolor: '#fff1f2', color: '#be123c', borderRadius: '16px', p: 2, mb: 2, fontSize: 13 }}>
          Failed to load — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Box>
      )}
      {presetKey === 'custom' && !customReady && (
        <Box sx={{ border: '1px solid #fde68a', bgcolor: '#fffbeb', color: '#b45309', borderRadius: '16px', p: 2, mb: 2, fontSize: 13 }}>
          Pick a start and end date to load the custom range.
        </Box>
      )}

      {/* KPI row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
        <GxKpiCard label="Companies" icon={<ApartmentIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.indigo}
          value={data ? gxInt(data.companies) : loadingValue} sub="Distinct companies reached · click"
          onClick={toggleCompanyDrill} active={companyDrillOpen} />
        <GxKpiCard label="Contacts Reached" icon={<GroupsIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.teal}
          value={data ? gxInt(reached) : loadingValue} sub={data ? joinSub(cmp(reached, 'contactsReached'), 'click for contacts') : undefined}
          onClick={() => toggleKpiDrill('sent', 'Contacts Reached')} active={kpiDrill?.metric === 'sent'} />
        <GxKpiCard label="Emails Delivered" icon={<SendIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.amber}
          value={data ? gxInt(data.emailsSent ?? data.emailsDelivered) : loadingValue}
          sub={data ? joinSub(cmp(data.emailsSent ?? data.emailsDelivered, 'emailsSent'), 'lifetime, to reached contacts') : undefined} />
        <GxKpiCard label="Opened" icon={<DraftsIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.indigo}
          value={data ? gxInt(data.opened) : loadingValue} sub={data ? joinSub(cmp(data.opened, 'opened'), rateOfReached(data.opened)) : undefined}
          onClick={() => toggleKpiDrill('opened', 'Opened')} active={kpiDrill?.metric === 'opened'} />
        <GxKpiCard label="Clicked" icon={<AdsClickIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.teal}
          value={data ? gxInt(data.clicked) : loadingValue} sub={data ? joinSub(cmp(data.clicked, 'clicked'), rateOfReached(data.clicked)) : undefined}
          onClick={() => toggleKpiDrill('clicked', 'Clicked')} active={kpiDrill?.metric === 'clicked'} />
        <GxKpiCard label="Replied" icon={<ReplyIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.pink}
          value={data ? gxInt(data.replied) : loadingValue} sub={data ? joinSub(cmp(data.replied, 'replied'), rateOfReached(data.replied)) : undefined}
          onClick={() => toggleKpiDrill('replied', 'Replied')} active={kpiDrill?.metric === 'replied'} />
      </Box>

      {kpiDrill && <Box sx={{ mb: 3 }}><InlineContactTable request={kpiDrill} onClose={() => setKpiDrill(null)} /></Box>}
      {companyDrillOpen && <Box sx={{ mb: 3 }}><InlineCompanyTable period={rangeParams.period} from={rangeParams.from} to={rangeParams.to} onClose={() => setCompanyDrillOpen(false)} /></Box>}

      {/* Engagement funnel — tapering stages, each clickable; Bounced is a side badge. */}
      <Box sx={{ mt: 1 }}>
        <GxSectionCard
          title="Engagement Funnel" icon={<MailOutlineIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.indigo}
          right={(
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              {bounced && (
                <Box component="button" onClick={() => toggleFunnelDrill('bounced', 'Bounced')}
                  title="Delivery-quality signal, not a funnel stage — bounces come off Sent directly."
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    border: '1px solid rgba(225,29,72,0.3)', color: GX_NEGATIVE, bgcolor: 'rgba(225,29,72,0.06)', borderRadius: '9999px', px: 1.25, py: 0.5,
                  }}>
                  <WarningAmberIcon sx={{ fontSize: 13 }} /> {gxInt(bounced.count)} bounced ({fmtRate(bounced.pctOfSent)})
                </Box>
              )}
              {data?.window && <GxPill color={GX_COLORS.indigo}>Selected: {windowText}</GxPill>}
            </Box>
          )}
        >
          <Box sx={{ maxWidth: 672, mx: 'auto', pt: 1 }}>
            {taper.map((f) => (
              <ClickableFunnelStage
                key={f.stage} width={f.width}
                onClick={() => toggleFunnelDrill(FUNNEL_METRIC_KEY[f.stage] || 'sent', FUNNEL_DISPLAY_LABEL[f.stage] || f.stage)}
                icon={FUNNEL_META[f.stage].icon} label={FUNNEL_DISPLAY_LABEL[f.stage] || f.stage}
                value={gxInt(f.count)} pct={f.pctOfSent != null ? Number(f.pctOfSent).toFixed(1) : '—'}
                gradient={FUNNEL_META[f.stage].gradient}
              />
            ))}
            {!data && !isFetching && (
              <Typography sx={{ fontSize: 13, color: GX_INK_500, textAlign: 'center' }}>No data yet — run a HubSpot sync from Data → Connectors.</Typography>
            )}
          </Box>
          {funnelDrill && <Box sx={{ mt: 2.5 }}><InlineContactTable request={funnelDrill} onClose={() => setFunnelDrill(null)} /></Box>}
        </GxSectionCard>
      </Box>

      {!data && (
        <Box sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '16px', p: 3, textAlign: 'center', color: GX_INK_500, fontSize: 13, mb: 3 }}>
          Loading segment tables…
        </Box>
      )}
      {data && (
        <>
          <SegmentTable
            key={`category-${qsKey}`} title="By Category" segmentLabel="Category" rows={data.byCategory || []}
            icon={<CategoryIcon sx={{ fontSize: 16 }} />}
            makeRequest={(key) => makeDrill('sent', `Category: ${key}`, { category: key })}
          />
          <SegmentTable
            key={`leader-${qsKey}`} title="By Sales POC" segmentLabel="Sales POC" rows={data.bySalesPoc || []}
            icon={<PeopleAltIcon sx={{ fontSize: 16 }} />}
            makeRequest={(key) => makeDrill('sent', `Leader: ${key}`, { leader: key })}
          />
          <MatrixTable leaders={data.pivotPocNames || []} rows={data.categoryByPoc || []} />
        </>
      )}

      {data?.config && (
        <Typography sx={{ fontSize: 11, color: GX_INK_500, mb: 2 }}>
          Outreach start {data.config.outreachStartDate} · Category / Sales POC parsed from each contact&apos;s last
          campaign name (the reference also applied a historical sheet override for contacts last emailed before
          {' '}{data.config.sheetAttributionCutoff}; that one-time sheet import isn&apos;t available here).
        </Typography>
      )}

      <GxRequestCampaignSection />
    </Box>
  )
}
